import { inject, Injectable, signal } from '@angular/core';
import { NavigationExtras, Router, UrlTree } from '@angular/router';

const SHOW_NAV_PARAM = 'showNav';

@Injectable({ providedIn: 'root' })
export class ShowNavService {
  private _router = inject(Router);
  private _isActive = signal(false);

  public readonly isActive = this._isActive.asReadonly();

  constructor() {
    const url = new URL(window.location.href);
    if (url.searchParams.has(SHOW_NAV_PARAM)) {
      this._isActive.set(true);
      this._patchRouter();
    }
  }

  private _patchRouter(): void {
    const originalNavigate = this._router.navigate.bind(this._router);
    const originalNavigateByUrl = this._router.navigateByUrl.bind(this._router);

    this._router.navigate = (
      commands: unknown[],
      extras?: NavigationExtras
    ): Promise<boolean> => {
      // If a single command contains '?' (e.g. from AuthService storedRoute),
      // it's a full URL string — route through navigateByUrl which handles it correctly
      if (
        commands.length === 1 &&
        typeof commands[0] === 'string' &&
        commands[0].includes('?')
      ) {
        return this._router.navigateByUrl(commands[0] as string, extras);
      }

      if (
        extras?.queryParamsHandling !== 'preserve' &&
        !extras?.queryParams?.[SHOW_NAV_PARAM]
      ) {
        extras = { ...extras };
        extras.queryParams = { ...extras.queryParams, [SHOW_NAV_PARAM]: true };
      }
      return originalNavigate(commands, extras);
    };

    this._router.navigateByUrl = (
      url: string | UrlTree,
      extras?: NavigationExtras
    ): Promise<boolean> => {
      if (typeof url === 'string') {
        if (!url.includes(SHOW_NAV_PARAM)) {
          const [path, fragment] = url.split('#');
          const separator = path.includes('?') ? '&' : '?';
          url = path + separator + SHOW_NAV_PARAM + '=true' + (fragment ? '#' + fragment : '');
        }
      } else if (!url.queryParams?.[SHOW_NAV_PARAM]) {
        url.queryParams = { ...url.queryParams, [SHOW_NAV_PARAM]: 'true' };
      }
      return originalNavigateByUrl(url, extras);
    };
  }
}
